import React, { useMemo } from "react";
import PlayPreviewButton from "../../../../components/PlayPreviewButton";

import { useDispatch, useSelector } from "react-redux";
import SectionItem from "../../../../components/section/section-item/SectionItem";
import TimeWindows, { generateTimeWindowsText } from "../../../../components/time-windows/TimeWindows";
import {
  updateIsSameAsOpeningHours,
  updateSchedulingWindows,
} from "../../../../redux/actions/CalendarManagementActions";
import { getFeatureFlag, scrollToFirstErrorElement } from "../../../../services-logic/base-logic";
import {
  KNOWLEDGE_BASE_KEY,
  OPENING_HOURS_KEY,
  sortKnowledgeBaseItems,
} from "../../../../services-logic/knowledge-base/knowledge-base-logic";
import MultiToggleOptions from "../../../../components/toggles/MultiToggleOptions";
import { isServiceAllowed } from "../../../../utils/utilServices";
import { updateKnowledgeBaseItems, updateOpenKnowledgeBaseItems } from "../../../../redux/actions/KnowledgeBaseActions";

const SchedulingWindowsSection = () => {
  const dispatch = useDispatch();
  const { updatedCalendarInfo, updatedSchedulingWindows, config, updatedIsSameAsOpeningHours } = useSelector(
    (state) => state.calendarManagement
  );
  const { updatedKnowledgeBaseItems } = useSelector((state) => state.knowledgeBase);
  const updatedVoice = useSelector((state) => state.info.updatedVoice);
  const agentLanguage = useSelector((state) => state.info.agentLanguage);
  const voices = useSelector((state) => state.info.voices);
  const selectedVoice = voices?.find((voice) => voice.id === updatedVoice);
  const interval = parseInt(getFeatureFlag("SR-SNIPPET", "Organization.Snippet.schedulingHoursInterval", 30));

  const toggleOptions = ["Same as opening hours", "Custom hours"];

  const openingHours = useMemo(
    () => updatedKnowledgeBaseItems.find((item) => item.type === OPENING_HOURS_KEY)?.value || [],
    [updatedKnowledgeBaseItems]
  );

  const onSchedulingWindowsUpdated = (newSchedulingWindows) => {
    dispatch(updateSchedulingWindows(newSchedulingWindows));
  };

  const onToggleSameAsOpeningHours = (selected) => {
    const isSameAsOpeningHours = selected === toggleOptions[0];
    dispatch(updateIsSameAsOpeningHours(isSameAsOpeningHours));
  };

  const jumpToOpeningHours = () => {
    // if opening hours doesn't exist, add it.
    const openingHoursItem = updatedKnowledgeBaseItems.find((item) => item.type === OPENING_HOURS_KEY);
    if (!openingHoursItem) {
      updatedKnowledgeBaseItems.push({
        priority: 2,
        title: "Opening hours",
        type: OPENING_HOURS_KEY,
        value: [],
        errors: { title: "", value: "" },
      });
    }
    const sortedItems = sortKnowledgeBaseItems(updatedKnowledgeBaseItems);
    dispatch(updateKnowledgeBaseItems(sortedItems));

    const index = sortedItems.findIndex((item) => item.type === OPENING_HOURS_KEY);
    dispatch(updateOpenKnowledgeBaseItems([index]));

    setTimeout(() => scrollToFirstErrorElement(`knowledge-item-${index}`), 100);
  };

  const schedulingWindowsContent = useMemo(
    () => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 text-section-description-sm md:text-section-description-md lg:text-section-description-lg font-section-description">
          <MultiToggleOptions
            options={toggleOptions}
            selectedOption={updatedIsSameAsOpeningHours ? toggleOptions[0] : toggleOptions[1]}
            setSelectedOption={onToggleSameAsOpeningHours}
          />
          {updatedIsSameAsOpeningHours && openingHours?.length === 0 && (
            <span className="text-red-400">
              Opening hours are not configured, click{" "}
              <a className="text-primary hover:underline cursor-pointer" onClick={jumpToOpeningHours}>
                here
              </a>{" "}
              to configure.
            </span>
          )}

          {updatedIsSameAsOpeningHours && openingHours?.length > 0 && (
            <span>
              Scheduling hours will match your opening hours, click{" "}
              <a className="text-primary hover:underline cursor-pointer" onClick={jumpToOpeningHours}>
                here
              </a>{" "}
              to view them.
            </span>
          )}
        </div>
        {!updatedIsSameAsOpeningHours && (
          <TimeWindows
            id="scheduling-windows"
            timeWindows={updatedSchedulingWindows}
            setTimeWindows={onSchedulingWindowsUpdated}
            minRows={1}
            maxRows={config.maxSchedulingWindowsRows}
            minHoursOptionsRows={config.minHoursOptionsRows}
            maxHoursOptionsRows={config.maxHoursOptionsRows}
            interval={interval}
          />
        )}
      </div>
    ),
    [updatedSchedulingWindows, config, updatedIsSameAsOpeningHours, interval, openingHours]
  );

  const titleActions = useMemo(
    () => (
      <PlayPreviewButton
        voice={selectedVoice?.id}
        textToPlay={generateTimeWindowsText(updatedSchedulingWindows, "Our scheduling windows are")}
        id="scheduling-windows-preview"
        disabled={updatedSchedulingWindows.length === 0 || selectedVoice?.enablePreview === false}
        language={selectedVoice?.language || agentLanguage}
      />
    ),
    [updatedSchedulingWindows, config, updatedVoice]
  );

  if (!updatedCalendarInfo?.id) return null;

  return (
    <SectionItem
      title="Scheduling hours"
      subtitle="Set the days and times when customers can book appointments with you."
      titleActions={titleActions}
      content={schedulingWindowsContent}
    />
  );
};

export default SchedulingWindowsSection;
